import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  DOCUMENTS_QUERY_KEY,
  LIST_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'
import {
  CrunchesPageEntities,
  shouldInvalidateCrunchesPage,
} from '@dc-shared-web/hooks/utils/shouldInvalidateCrunchesPage'

type RevokeSharedDocumentParams = {
  id: number | string
  email?: string
}

type RevokeSharedDocumentResponse = {
  status: 'error' | 'success'
  message?: string
  error?: string
}

const revokeSharedDocument = async (
  apiClient: AxiosInstance,
  params: RevokeSharedDocumentParams
): Promise<RevokeSharedDocumentResponse> => {
  const { data } = await apiClient.postForm(
    `/ajax/share-link/revoke/${params.id}`,
    {
      id: params.id,
    }
  )

  return data.data
}

export const useRevokeSharedDocument = (params: {
  documentId: string
  documentName: string
  onSuccess?: (email?: string) => void
  onError?: () => void
}) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: RevokeSharedDocumentParams) =>
      revokeSharedDocument(apiClient, mutationParams),
    {
      onSuccess: async (_, successParams) => {
        await queryClient.invalidateQueries<CrunchesPageEntities>({
          queryKey: [DOCUMENTS_QUERY_KEY, LIST_QUERY_KEY],
          refetchPage: (page) =>
            shouldInvalidateCrunchesPage(page, params.documentId),
        })

        toast({
          variant: 'success',
          title: 'Share link revoked',
          description: `Share link to "${params.documentName}" has been revoked for ${successParams.email}.`,
        })
        params.onSuccess?.(successParams?.email)
      },
      onError: () => {
        toast({
          variant: 'error',
          title: `Failed to revoke "${params.documentName}".`,
          description: 'If the issue persists, please contact support.',
        })
        params.onError?.()
      },
    }
  )
}
